import type { Bet } from "@/shared/model/bet";
import type { Leg, LegOptions } from "@/shared/model/leg";
import type { OutcomeSlip } from "@/shared/model/outcome";
import { SlipBuildDataResponse } from "@/api/interfaces/slip-build";
import { PriceChangeBehaviour } from "@/shared/model/ticket";
import { PriceSlip } from "@/shared/model/price";
import { cloneDeep as _cloneDeep } from "lodash-es";
import { BetServiceResponse } from "@/api/interfaces/bet-service-response";
import { PotentialPrice } from "@/shared/model/potential-price";
import { PotentialPayout } from "@/shared/model/potential-payout";

// Fking OB and their endpoints... look at this mess.
// It's another place where we need to create new interfaces for 'base' objects.
interface Bet2Bet extends Pick<Bet, "betTypeRef" | "documentId" | "legRefs" | "lines"> {
    options: {
        priceChangeBehaviour: PriceChangeBehaviour;
        winPlaceOverride: string; // can be anything else than "WIN"???
    };
    stake: string; // I would expect number but in specification is string... we'll see
}

interface LegOptions2Bet extends Pick<LegOptions, "banker" | "handicaps" | "inMultiples"> {
    winPlace: string; // can be anything else than "WIN"???
    prices: PriceSlip[];
}

interface Leg2Bet extends Pick<Leg, "documentId" | "outcomes"> {
    options: LegOptions2Bet;
}

interface OutcomeSlip2Bet extends Pick<OutcomeSlip, "id"> {
    prices: Omit<PriceSlip, "winPlaceRef">[];
}

interface ISlipBetData {
    bets: Bet2Bet[];
    legs: Leg2Bet[];
    outcomes: OutcomeSlip2Bet[];
}

export class SlipBetData implements ISlipBetData {
    public bets: Bet2Bet[];
    public legs: Leg2Bet[];
    public outcomes: OutcomeSlip2Bet[];

    public constructor(data: SlipBuildDataResponse, priceChangeBehaviour: PriceChangeBehaviour, stakes: number[]) {
        const _data = _cloneDeep(data); // Strip all (nested) refs
        this.bets = _data.bets.map((b, idx) => {
            const filteredSystemBets = !b?.systemBets
                ? undefined
                : b.systemBets
                      .map((sb, i) => ({
                          betTypeRef: sb.betTypeRef,
                          lines: sb.lines,
                          stake: stakes[i] ? stakes[i].toString() : "0",
                          stakeType: "SPL",
                      }))
                      .filter((sb) => sb.stake !== "0");
            return {
                betTypeRef: b.betTypeRef,
                documentId: b.documentId,
                legRefs: b.legRefs,
                // BEWARE: There is a validation on OB API which checks number of lines with sum of lines in systemBets
                lines: filteredSystemBets?.reduce((count: number, sb) => count + sb.lines, 0) || b.lines,
                options: {
                    priceChangeBehaviour,
                    winPlaceOverride: "WIN", // Not returned by /build ep... where should I take it???
                },
                stake: !b?.systemBets
                    ? stakes[idx].toFixed(2)
                    : b.systemBets
                          .reduce((count: number, sb, idx) => {
                              return count + (sb.lines * stakes[idx] || 0);
                          }, 0)
                          .toFixed(2),
                stakeType: !b?.systemBets ? undefined : "TOTAL",
                systemBets: filteredSystemBets,
            } as Bet2Bet;
        });
        this.legs = _data.legs.map(
            (l) =>
                ({
                    documentId: l.documentId,
                    outcomes: l.outcomes,
                    options: {
                        banker: l.options.banker,
                        handicaps: l.options.handicaps,
                        inMultiples: l.options.inMultiples,
                        prices: l.options.prices.map(
                            (p) =>
                                ({
                                    priceTypeRef: p.priceTypeRef,
                                    denominator: p.denominator,
                                    numerator: p.numerator,
                                    winPlaceRef: "WIN", // Not returned by /build ep... where should I take it???
                                }) as PriceSlip,
                        ),
                        winPlace: l.options.winPlace,
                        group: l.options?.group,
                        combinationRef: l.options?.combinationRef,
                    } as LegOptions2Bet,
                }) as Leg2Bet,
        );
        this.outcomes = _data.outcomes?.map(
            (o) =>
                ({
                    id: o.id,
                    prices: o.prices.map((p) => ({
                        priceTypeRef: p.priceTypeRef,
                        denominator: p.denominator,
                        numerator: p.numerator,
                    })),
                }) as OutcomeSlip2Bet,
        );
    }
}

export enum SlipBetStatus {
    Placed = "PLACED",
    ReferralPending = "REFERRAL_PENDING",
    BirDelay = "BIR_DELAY",
    Failed = "FAILED",
}

interface SlipBetDataResponse extends Omit<ISlipBetData, "bets" | "outcomes"> {
    bets: {
        documentId: string;
        id: string;
        betTypeRef: string;
        stakeType: string;
        text: string;
        status: SlipBetStatus;
        stake: string;
        lines: number;
        legRefs: string[];
        potentialPayout: PotentialPayout[];
        potentialPrice: PotentialPrice;
        receipt: string;
        options: {
            winPlaceOverride: PriceChangeBehaviour;
            priceChangeBehaviour: string;
        };
    }[];
    bonusStake: string;
    id: string;
    outcomes: OutcomeSlip[];
    placedDate: string;
    poll?: boolean;
    pollPeriod?: number;
    potentialPayoutValues: {
        bonus: string;
        grossReturns: string;
        maxWinnings: string;
        returns: string;
        totalWinnings: string;
    };
    stake: string;
    status: SlipBetStatus;
    tokenStake: string;
    validityPeriod: number;
}

export interface SlipBet extends BetServiceResponse {
    data: SlipBetDataResponse;
}
